import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';
import { QUERY_KEYS, useAnalytics } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';
import { useJobPositionsService } from '@restworld/data-services';
import { showToast } from '@restworld/ui-ds-base';

import { isConflictError } from './use-put-offline-job-position';

// Cancels a going-offline scheduled at the end of the PPU paid period: the JP stays online.
export function useCancelScheduledOffline() {
  const queryClient = useQueryClient();
  const jobPositionsService = useJobPositionsService();
  const { t } = useTranslation('job-positions-page');
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();

  const { mutate, isPending: isCancelling } = useMutation({
    mutationFn: (jobPositionId: string) => jobPositionsService.cancelScheduledOfflineJobPosition(jobPositionId),
    onSuccess: (_data, jobPositionId) => {
      trackEvent('employer_jp_offline_schedule_cancelled', {
        jp_id: jobPositionId,
        organization_id: user?.organization_id,
      });
      showToast(t('scheduled_offline_cancelled_toast'), 'success', {
        position: 'top-right',
        autoClose: 3000,
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId] });
    },
    onError: (error, jobPositionId) => {
      // 409: nothing was scheduled any more (e.g. the JP already went offline).
      showToast(
        t(isConflictError(error) ? 'scheduled_offline_already_offline' : 'scheduled_offline_cancel_error'),
        'error',
        {
          position: 'top-right',
          autoClose: 3000,
        }
      );
      // Either way, refetch so the card and the header show the real state.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.LIST] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB_POSITIONS.DETAILS, jobPositionId] });
    },
  });

  const cancelScheduledOffline = useCallback((jobPositionId: string) => mutate(jobPositionId), [mutate]);

  return { cancelScheduledOffline, isCancelling };
}

export default useCancelScheduledOffline;
